import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AccessPolicyUpdate,
  clearToolAccessPolicy,
  getToolAccessPolicy,
  getToolActionAccessPolicy,
  getToolsBaseAccessPolicy,
  updateToolAccessPolicy,
  updateToolActionAccessPolicy,
  updateToolsBaseAccessPolicy,
} from "@/api/admin-api"
import { api } from "@/lib/api-client"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// Agent Tools Management
export function useAdminAgentsQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "agents"],
    queryFn: async () => {
      const response = await api.get("/admin/agents")
      const data = response.data || response
      return Array.isArray(data) ? data : []
    },
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useAdminAgentToolsQuery(agentName: string, enabled = true) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "tools"],
    queryFn: async () => {
      const response = await api.get(`/admin/agents/${agentName}/tools`)
      const data = response.data || response
      return Array.isArray(data) ? data : []
    },
    enabled: enabled && !!agentName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useAdminToolAccessPolicyQuery(
  agentName: string,
  toolName: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "tools", toolName, "access"],
    queryFn: () => getToolAccessPolicy(agentName, toolName ?? ""),
    enabled: enabled && !!agentName && !!toolName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminToolsBaseAccessPolicyQuery(agentName: string, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "tools", "base", "access"],
    queryFn: () => getToolsBaseAccessPolicy(agentName),
    enabled: enabled && !!agentName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateToolAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      toolName,
      policy,
    }: {
      agentName: string
      toolName: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateToolAccessPolicy(agentName, toolName, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools", variables.toolName, "access"],
      })
    },
  })
}

export function useUpdateToolsBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      policy,
    }: {
      agentName: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateToolsBaseAccessPolicy(agentName, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools", "base", "access"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools"],
      })
    },
  })
}

export function useClearToolAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ agentName, toolName }: { agentName: string; toolName: string }) => {
      return await clearToolAccessPolicy(agentName, toolName)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools"],
      })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools", variables.toolName, "access"],
      })
    },
  })
}

export function useAdminToolActionAccessPolicyQuery({
  agentName,
  toolName,
  actionName,
  enabled,
}: {
  agentName: string
  toolName: string | undefined
  actionName: string | undefined
  enabled: boolean
}) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "tools", toolName, "actions", actionName, "access"],
    queryFn: () => getToolActionAccessPolicy(agentName, toolName ?? "", actionName ?? ""),
    enabled: enabled && !!agentName && !!toolName && !!actionName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateToolActionAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      toolName,
      actionName,
      policy,
    }: {
      agentName: string
      toolName: string
      actionName: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateToolActionAccessPolicy({ agentName, toolName, actionName, policy })
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "agents",
          variables.agentName,
          "tools",
          variables.toolName,
          "actions",
          variables.actionName,
          "access",
        ],
      })
    },
  })
}

export function useUpdateToolStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      toolName,
      enabled,
    }: {
      agentName: string
      toolName: string
      enabled: boolean
    }) => {
      const response = await api.put(`/admin/agents/${agentName}/tools/${toolName}/status`, {
        enabled,
      })
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update tool status")
      }
      return data
    },
    onMutate: async ({ agentName, toolName, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "agents", agentName, "tools"] })

      const previousTools = queryClient.getQueryData<Array<{ name: string; enabled: boolean }>>([
        "admin",
        "agents",
        agentName,
        "tools",
      ])

      if (previousTools) {
        queryClient.setQueryData(
          ["admin", "agents", agentName, "tools"],
          previousTools.map((tool) => (tool.name === toolName ? { ...tool, enabled } : tool))
        )
      }

      return { previousTools, agentName }
    },
    onError: (err, variables, context) => {
      if (context?.previousTools) {
        queryClient.setQueryData(
          ["admin", "agents", context.agentName, "tools"],
          context.previousTools
        )
      }
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools"],
      })
    },
  })
}

// Tool Actions Management
export function useAdminToolActionsQuery(agentName: string, toolName: string, enabled = true) {
  return useQuery({
    queryKey: ["admin", "agents", agentName, "tools", toolName, "actions"],
    queryFn: async () => {
      const response = await api.get(`/admin/agents/${agentName}/tools/${toolName}/actions`)
      const data = response.data || response
      return Array.isArray(data) ? data : []
    },
    enabled: enabled && !!agentName && !!toolName,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useUpdateActionStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      toolName,
      actionName,
      enabled,
    }: {
      agentName: string
      toolName: string
      actionName: string
      enabled: boolean
    }) => {
      const response = await api.put(
        `/admin/agents/${agentName}/tools/${toolName}/actions/${actionName}/status`,
        { enabled }
      )
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update action status")
      }
      return data
    },
    onMutate: async ({ agentName, toolName, actionName, enabled }) => {
      await queryClient.cancelQueries({
        queryKey: ["admin", "agents", agentName, "tools", toolName, "actions"],
      })

      const previousActions = queryClient.getQueryData<Array<{ name: string; enabled: boolean }>>([
        "admin",
        "agents",
        agentName,
        "tools",
        toolName,
        "actions",
      ])

      if (previousActions) {
        queryClient.setQueryData(
          ["admin", "agents", agentName, "tools", toolName, "actions"],
          previousActions.map((action) =>
            action.name === actionName ? { ...action, enabled } : action
          )
        )
      }

      return { previousActions, agentName, toolName }
    },
    onError: (err, variables, context) => {
      if (context?.previousActions) {
        queryClient.setQueryData(
          ["admin", "agents", context.agentName, "tools", context.toolName, "actions"],
          context.previousActions
        )
      }
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools", variables.toolName, "actions"],
      })
    },
  })
}

export function useUpdateActionsStatusBatchMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentName,
      toolName,
      updates,
    }: {
      agentName: string
      toolName: string
      updates: Record<string, boolean>
    }) => {
      const response = await api.put(
        `/admin/agents/${agentName}/tools/${toolName}/actions/status/batch`,
        { updates }
      )
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to batch update action statuses")
      }
      return data
    },
    onMutate: async ({ agentName, toolName, updates }) => {
      await queryClient.cancelQueries({
        queryKey: ["admin", "agents", agentName, "tools", toolName, "actions"],
      })

      const previousActions = queryClient.getQueryData<Array<{ name: string; enabled: boolean }>>([
        "admin",
        "agents",
        agentName,
        "tools",
        toolName,
        "actions",
      ])

      if (previousActions) {
        queryClient.setQueryData(
          ["admin", "agents", agentName, "tools", toolName, "actions"],
          previousActions.map((action) =>
            action.name in updates ? { ...action, enabled: updates[action.name] } : action
          )
        )
      }

      return { previousActions, agentName, toolName }
    },
    onError: (err, variables, context) => {
      if (context?.previousActions) {
        queryClient.setQueryData(
          ["admin", "agents", context.agentName, "tools", context.toolName, "actions"],
          context.previousActions
        )
      }
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools", variables.toolName, "actions"],
      })
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "agents", variables.agentName, "tools", variables.toolName, "actions"],
      })
    },
  })
}
